import React, { useState } from 'react';
import { AnonymousPlayerSetupRequest, AnonymousPlayerSetupResponse } from '../../../../shared/types/game'; 
import { useNavigate } from 'react-router-dom';
import { Socket } from 'socket.io-client';
import styles from './PlayerNameSetup.module.css';

interface PlayerNameSetupProps {
  onNameSet: (name: string, playerId: string) => void;
  socket: Socket | null;
  isSocketConnected: boolean;
  isPlayerConnectedToServer: boolean;
}

const PlayerNameSetup: React.FC<PlayerNameSetupProps> = ({ onNameSet, socket, isSocketConnected, isPlayerConnectedToServer }) => {
  const [inputName, setInputName] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const navigate = useNavigate();

  const handleSaveName = () => {
    setErrorMessage('');
    setSuccessMessage('');

    if (inputName.trim() === '') {
      setErrorMessage('プレイヤー名を入力してください。');
      return;
    }

    if (!socket || !isSocketConnected || !isPlayerConnectedToServer) {
      setErrorMessage('サーバーに接続していません。しばらく待ってから再度お試しください。');
      console.log('PlayerNameSetup: ソケット未接続またはプレイヤー未接続。', { socket, isSocketConnected, isPlayerConnectedToServer });
      return;
    }

    setIsLoading(true);

    const request: AnonymousPlayerSetupRequest = {
      name: inputName.trim(), 
    };

    socket.emit('player:setupName', request, (response: AnonymousPlayerSetupResponse) => {
      setIsLoading(false);

      if (response.success && response.player) {
        const { id: playerId, name: registeredName } = response.player;
        localStorage.setItem('shogiPlayerId', playerId);
        localStorage.setItem('shogiPlayerName', registeredName);
        setSuccessMessage(`「${registeredName}」としてゲストプレイを開始します！`);
        onNameSet(registeredName, playerId);
      } else {
        let specificErrorMessage = 'プレイヤー登録に失敗しました。';
        if (response.message) {
          specificErrorMessage = `プレイヤー登録に失敗しました: ${response.message}`;
          if (response.message.includes('既に存在する') || response.message.includes('Duplicate key')) {
            specificErrorMessage = `このプレイヤー名「${inputName.trim()}」は既に使われています。別の名前をお試しください。`;
          }
        }
        setErrorMessage(specificErrorMessage);
        console.error('PlayerNameSetup: プレイヤー登録失敗:', response.message);
      }
    });
  };

  const handleNavigateToAuth = () => {
    navigate('/auth');
  };

  return (
    <div className={styles.container}>
      <h2 className={styles.h2}>さあ、将棋を始めましょう！</h2>
      <p className={styles.descriptionText}>
        まずは、対局で使用する**プレイヤー名**を設定してください。<br />
        アカウント登録なしで、ゲストとしてプレイし続けることも可能です。<br />
        後からいつでもアカウントを作成し、この名前で登録したり、新しい名前を設定したりできます。
      </p>
      <div className={styles.inputGroup}>
        <input
          type="text"
          value={inputName}
          onChange={(e) => setInputName(e.target.value)}
          placeholder="プレイヤー名を入力"
          disabled={isLoading}
          className={styles.nameInput}
          maxLength={20}
        />
        <button onClick={handleSaveName} disabled={isLoading || inputName.trim() === ''} className={styles.saveButton}>
          {isLoading ? '設定中...' : '名前を決定（ゲストプレイ）'}
        </button>
      </div>
      {isLoading && <p className={styles.loadingMessage}>サーバーと通信中...</p>}
      {errorMessage && <p className={styles.errorMessage}>{errorMessage}</p>}
      {successMessage && <p className={styles.message}>{successMessage}</p>}

      <div className={styles.sectionDivider}></div>

      <div className={styles.authSection}>
        <h3 className={styles.h3}>すでにアカウントをお持ちですか？</h3>
        <button onClick={handleNavigateToAuth} disabled={isLoading} className={styles.authButton}>
          ログイン / 新規登録
        </button>
      </div>
    </div>
  );
};

export default PlayerNameSetup;